import {
  agencyEnum,
  clearCacheEnum,
  countryEnum,
  datatype,
  Environment,
  filterDetail,
  filtertype,
  localQuery,
  metric,
  metricFunction,
  queryInput,
} from 'API';
import AuthContext from 'context/AuthContext';
import { useCampaignContext } from 'context/CampaignContext';
import { useClientContext } from 'context/ClientContext';
import { PublishContext } from 'context/PublishContext';
import { JsonObject, useDataQuery } from 'customHooks/useDataQuery';
import { getEndDate, getStartDate } from 'methods/dateMethods';
import React, { useContext, useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useTheme } from 'styled-components';
import { KPIContainer, Kpis, TxtContainer } from './Main.styles';
import { getQueryFilters } from 'methods/queryFiltersMethod';
import APIContext from 'context/APIContext';

type Props = {};

const DigitalKPIs = (props: Props) => {
  const { user, userIsASolutionsArchitect } = useContext(AuthContext);
  const { activeCampaign, canDoInsourceAndOnInsourcePortal, dashType } = useCampaignContext();
  const { activeOrganization } = useClientContext();
  const { publishedData, updateOLArray } = useContext(PublishContext);
  const { REACT_APP_AGENCY } = process.env;
  const { getEnvironment } = useContext(APIContext);

  const username = user?.username;
  const organization = activeOrganization?.Organization;
  const ClientCountry = activeCampaign?.ClientCountry;
  const isDemo = organization === 'DEMO-Organization';
  const theme = useTheme();
  const [query, setQuery] = useState<queryInput>();
  const [cacheStatus, setCacheStatus] = useState<'idle' | 'clearing' | 'cleared'>('idle');

  // Determine position based on dashType
  const kpiPosition = dashType === 'SEARCH' ? 'Search0' : '0';

  // in publishedData find the object with position based on dashType
  const publishedKPI = publishedData?.find((element: any) => element.position === kpiPosition);
  const publishedOutputLocation = publishedKPI?.outputlocation;
  const isStaging = getEnvironment === Environment.STAGING;

  useEffect(() => {
    if (
      activeCampaign &&
      Object.keys(activeCampaign).length > 0 &&
      (activeCampaign?.authorized === true ||
        (canDoInsourceAndOnInsourcePortal && activeCampaign?.authorized === false))
    ) {
      //METRICS DATA
      const metrics: metric[] = [
        {
          column: 'cost',
          name: 'TotalCost',
          aggregation: metricFunction.SUM,
        },
        {
          column: 'clicks',
          name: 'TotalClicks',
          aggregation: metricFunction.SUM,
        },
        {
          column: 'impressions',
          name: 'TotalImpression',
          aggregation: metricFunction.SUM,
        },
      ];

      // FILTER(CAMPAIGN DATA)

      const filter: filterDetail = {
        column: 'campaign_id',
        filter: {
          filtertype: activeCampaign?.campaignId?.toString().startsWith('100') ? filtertype.IN : filtertype.EQUAL,
          datatype: datatype.NUMBER,
          [activeCampaign?.campaignId?.toString().startsWith('100') ? 'list' : 'value']: activeCampaign?.campaignId
            ?.toString()
            .startsWith('100')
            ? activeCampaign.childCampaigns?.map((e) => e?.campaignId.toString())
            : activeCampaign?.campaignId?.toString(),
        },
      };

      // DATE DATA
      const localQuery: localQuery = {
        // filters: [filter],
        filters: getQueryFilters(activeCampaign),
        metrics: metrics,
        dimensions: ['campaign_id'],
        preview: 300,
      };

      const getDatabase = (country: string | undefined) => {
        switch (country) {
          case 'DK':
            return 'data_lake_denmark';
          case 'SE':
            return 'data_lake_sweden';
          case 'FI':
            return 'data_lake_finland';
          default:
            return 'data_lake_norway';
        }
      };

      const getViewName = (platform: string) => {
        return isDemo ? `fozzie_${platform}_demo` : `fozzie_${platform}_${REACT_APP_AGENCY}`;
      };

      // Define platforms based on dashType
      const platforms =
        dashType === 'SEARCH'
          ? ['google_ads_search'] // Search specific platforms
          : [
              // Digital platforms (original)
              'facebook',
              'adform_olv',
              'adform_bvod_excluded',
              'campaign_manager_bvod_excluded',
              'google_dv360_youtube_excluded',
              'google_ads_video',
              'google_dv360_youtube',
              'tiktok',
              'snapchat',
              'linkedin',
              'readpeak',
              'taboola',
              'pinterest',
              'marathon_digital_tracking',
              'campaign_manager_olv',
              'google_ads_display',
              'tradedesk',
              'amazondsp',
            ];

      const views = platforms.map((platform) => ({
        viewName: getViewName(platform),
        database: getDatabase(ClientCountry),
      }));

      const newQuery: queryInput = {
        query: localQuery,
        views: views,
        jobnumber: 'ANNA0027',
        clients: [
          {
            country: countryEnum[activeCampaign?.ClientCountry as keyof typeof countryEnum],
            client: activeCampaign?.ClientCode as string,
          },
        ],
        date: { from: getStartDate(36), to: getEndDate() },
        user: {
          name: username,
          agency: agencyEnum[`${REACT_APP_AGENCY?.toUpperCase()}` as keyof typeof agencyEnum],
          country: countryEnum.DK,
        },
      };
      console.log('newQuery', newQuery);
      setQuery(newQuery);
    }
  }, [activeCampaign, REACT_APP_AGENCY, isDemo, ClientCountry, username, dashType, canDoInsourceAndOnInsourcePortal]);

  const [data, queryStatus, error, queryId, outputLocation] = useDataQuery({
    query: query,
    querySource: 'KPI',
    publishedOutputLocation: publishedOutputLocation,
  });

  const jsonDataObject = data as JsonObject;
  const totalClicks = jsonDataObject ? Math.trunc(Number(jsonDataObject.TotalClicks)) : NaN;
  const totalCost = jsonDataObject ? Math.trunc(Number(jsonDataObject.TotalCost)) : NaN;
  const totalImpression = jsonDataObject ? Number(jsonDataObject.TotalImpression) : NaN;
  const CPC = totalCost / totalClicks;
  const CPM = (totalCost / totalImpression) * 1000;

  const clearCache = () => {
    if (!query || cacheStatus === 'clearing') {
      return;
    }

    setTimeout(() => {
      setCacheStatus('clearing');
      setQuery({ ...query, clearCache: clearCacheEnum.CLEAR });
    }, 1000);
  };

  useEffect(() => {
    if (cacheStatus === 'clearing') {
      if (queryStatus === 'DONE') {
        setCacheStatus('cleared');
      } else if (queryStatus === 'FAILED') {
        setCacheStatus('idle');
      }
    }
  }, [cacheStatus, queryStatus]);

  useEffect(() => {
    if (queryStatus === 'RUNNING' && cacheStatus === 'cleared') {
      setCacheStatus('idle');
    }
  }, [queryStatus, cacheStatus]);

  useEffect(() => {
    if (outputLocation) {
      const placeholdersObject = {
        position: kpiPosition,
        outputlocation: outputLocation,
      };
      updateOLArray(placeholdersObject);
    }
  }, [outputLocation, updateOLArray, kpiPosition]);

  return (
    <>
      {query && userIsASolutionsArchitect && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <button
            type="button"
            onClick={clearCache}
            style={{
              marginLeft: '10px',
              padding: '5px 10px',
              backgroundColor:
                cacheStatus === 'cleared' ? '#28a745' : cacheStatus === 'clearing' ? '#ffc107' : '#ff6b6b',
              color: 'white',
              border: 'none',
              boxShadow: 'none',
              borderRadius: '4px',
              cursor: cacheStatus === 'clearing' ? 'not-allowed' : 'pointer',
            }}
            disabled={cacheStatus === 'clearing'}
          >
            {cacheStatus === 'clearing'
              ? 'Clearing...'
              : cacheStatus === 'cleared'
                ? 'Cache Cleared'
                : 'Clear Cache'}
          </button>
        </div>
      )}
      {queryStatus === 'FAILED' ? (
        <Kpis>
          <KPIContainer style={{ padding: '1vw' }}>
            <p>Something went wrong, please reload the page and try again </p>
          </KPIContainer>
        </Kpis>
      ) : (
        <Kpis>
          <KPIContainer>
            {queryStatus === 'RUNNING' ? (
              <TxtContainer className="loader">
                <ReactLoading type="spinningBubbles" color={theme.publishButton} />
              </TxtContainer>
            ) : (
              <TxtContainer>
                <h3>Cost</h3>
                <h4 className="tooltip">
                  {Intl.NumberFormat('en-DK', { notation: 'compact' }).format(totalCost)}
                  <span className="tooltiptext">{Intl.NumberFormat('en-DK').format(totalCost)}</span>
                </h4>
              </TxtContainer>
            )}
          </KPIContainer>

          <KPIContainer>
            {queryStatus === 'RUNNING' ? (
              <TxtContainer className="loader">
                <ReactLoading type="spinningBubbles" color={theme.publishButton} />
              </TxtContainer>
            ) : (
              <TxtContainer>
                <h3>Impressions</h3>
                <h4 className="tooltip">
                  {Intl.NumberFormat('en-DK', { notation: 'compact' }).format(totalImpression)}
                  <span className="tooltiptext">{Intl.NumberFormat('en-DK').format(totalImpression)}</span>
                </h4>
              </TxtContainer>
            )}
          </KPIContainer>

          <KPIContainer>
            {' '}
            {queryStatus === 'RUNNING' ? (
              <TxtContainer className="loader">
                <ReactLoading type="spinningBubbles" color={theme.publishButton} />
              </TxtContainer>
            ) : (
              <TxtContainer>
                <h3>CPM</h3>
                <h4 className="tooltip">
                  {new Intl.NumberFormat('en-DK', { maximumFractionDigits: 2 }).format(CPM)}
                  <span className="tooltiptext">{new Intl.NumberFormat('en-DK').format(CPM)}</span>
                </h4>
              </TxtContainer>
            )}
          </KPIContainer>

          <KPIContainer>
            {queryStatus === 'RUNNING' ? (
              <TxtContainer className="loader">
                <ReactLoading type="spinningBubbles" color={theme.publishButton} />
              </TxtContainer>
            ) : (
              <TxtContainer>
                <h3>Clicks</h3>
                <h4 className="tooltip">
                  {new Intl.NumberFormat('en-DK', { maximumFractionDigits: 2 }).format(totalClicks)}
                  <span className="tooltiptext">{totalClicks.toLocaleString('en-DK')}</span>
                </h4>
              </TxtContainer>
            )}
          </KPIContainer>

          <KPIContainer>
            {queryStatus === 'RUNNING' ? (
              <TxtContainer className="loader">
                <ReactLoading type="spinningBubbles" color={theme.publishButton} />
              </TxtContainer>
            ) : (
              <TxtContainer>
                <h3>CPC</h3>
                <h4 className="tooltip">
                  {new Intl.NumberFormat('en-DK', { maximumFractionDigits: 2 }).format(CPC)}
                  <span className="tooltiptext">{new Intl.NumberFormat('en-DK').format(CPC)}</span>
                </h4>
              </TxtContainer>
            )}
          </KPIContainer>
        </Kpis>
      )}
    </>
  );
};

export default DigitalKPIs;
